// Simple Message Variable Processor
// This is a simplified version that can be easily integrated into existing components

export interface MessageContext {
  contactName?: string; // Contact's first name
  contactFullName?: string; // Contact's full name (first + last)
  businessName?: string;
  businessContactName?: string; // kept for backward compatibility (first name)
  businessContactFirstName?: string;
  businessContactFullName?: string;
  businessPhone?: string;
  businessEmail?: string;
  appointmentTime?: string;
  appointmentDate?: string;
  uniqueAuthId?: string;
}

export interface MessageVariable {
  name: string;
  variable: string;
  description: string;
  category: 'contact' | 'business' | 'appointment' | 'links';
}

export class SimpleMessageVariableProcessor {
  private context: MessageContext;

  constructor(context: MessageContext = {}) {
    this.context = context;
  }

  /**
   * Process a message template and replace variables with actual values
   */
  processMessage(template: string): string {
    if (!template) return '';
    
    let processedMessage = template;
    
    // Get base URL from environment or use a default
    const baseUrl = window.location?.origin || 'https://tryeverybrand.com';
    
    // Function to generate a random token for one-time links
    const generateToken = (): string => {
      return Math.random().toString(36).substring(2, 15) + 
             Math.random().toString(36).substring(2, 15);
    };

    // Function to create secure, one-time links
    const createOneTimeLink = (path: string, text: string, params: Record<string, string> = {}) => {
      const token = generateToken();
      const queryParams = new URLSearchParams({
        ...params,
        token,
        ref: 'message-link',
        ts: Date.now().toString()
      });
      
      const url = `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}?${queryParams}`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer nofollow" style="text-decoration: none;">${text} </a>`;
    };

    // Define variable mappings - all fields from context object
    const variableMap: Record<string, string> = {
      // Contact variables
      ':contact_first_name:': this.context.contactName || 'Valued Customer',
      ':contact_full_name:': this.context.contactFullName || this.context.contactName || 'Valued Customer',
      
      // Business variables
      ':business_name:': this.context.businessName || 'Our Business',
      ':business_contact_name:': this.context.businessContactName || 
                               this.context.businessContactFirstName || 
                               'Our Team',
      ':business_contact_first_name:': this.context.businessContactFirstName || 
                                     this.context.businessContactName || 
                                     'Our Team',
      ':business_contact_full_name:': this.context.businessContactFullName || 
                                    (this.context.businessContactName ? 
                                      `${this.context.businessContactName} ${this.context.businessContactName}`.trim() : 
                                      'Our Team'),
      ':business_office_number:': this.context.businessPhone || '',
      ':business_phone:': this.context.businessPhone || '',
      ':business_email:': this.context.businessEmail || '',
      
      // Appointment variables
      ':appointment_time:': this.context.appointmentTime || '',
      ':appointment_date:': this.context.appointmentDate || '',
      
      // Special link variables
      ':new_post_highlight:': createOneTimeLink('/Dashboard', 'Home Feed Highlight', { 
        highlight: 'latest',
        businessId: this.context.uniqueAuthId || ''
      }),
      
      ':create_post_invite:': createOneTimeLink('/NewPost', 'Invite Contact to Post', {
        businessId: this.context.uniqueAuthId || '',
        source: 'message-invite'
      }),
      
      ':review_link:': createOneTimeLink('/ReviewInvite', 'Review Link', {
        businessId: this.context.uniqueAuthId || '',
        source: 'message'
      }),
      
      ':friend_invite_link:': createOneTimeLink('/InviteFriends', 'Friend Invite Link', {
        ref: 'business-referral',
        referrerId: this.context.uniqueAuthId || ''
      })
    };

    // Replace all variables in the template
    return Object.entries(variableMap).reduce((result, [variable, value]) => {
      const regex = new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      return result.replace(regex, value);
    }, template);
  }

  /**
   * Update the context with new data
   */
  updateContext(newContext: Partial<MessageContext>): void {
    this.context = { ...this.context, ...newContext };
  }

  /**
   * Get the current context
   */
  getContext(): MessageContext {
    return { ...this.context };
  }

  /**
   * Extract all variables from a message template
   */
  static extractVariables(template: string): string[] {
    const variableRegex = /:([a-zA-Z_][a-zA-Z0-9_]*):/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(template)) !== null) {
      variables.push(`:${match[1]}:`);
    }

    return [...new Set(variables)]; // Remove duplicates
  }

  /**
   * Validate a message template for unknown variables
   */
  static validateTemplate(template: string, knownVariables: string[] = []): { isValid: boolean; unknownVariables: string[] } {
    const extractedVariables = this.extractVariables(template);
    const unknownVariables = extractedVariables.filter(variable => !knownVariables.includes(variable));

    return {
      isValid: unknownVariables.length === 0,
      unknownVariables
    };
  }
}

// Utility functions for easy integration
export const processMessageVariables = (template: string, context: MessageContext): string => {
  const processor = new SimpleMessageVariableProcessor(context);
  return processor.processMessage(template);
};

export const extractMessageVariables = (template: string): string[] => {
  return SimpleMessageVariableProcessor.extractVariables(template);
};

export const validateMessageTemplate = (template: string, knownVariables: string[] = []): { isValid: boolean; unknownVariables: string[] } => {
  return SimpleMessageVariableProcessor.validateTemplate(template, knownVariables);
};

// Default variable list for UI components
export const DEFAULT_VARIABLES: MessageVariable[] = [
  { 
    name: 'Contact First Name', 
    variable: ':contact_first_name:', 
    description: "Recipient's first name",
    category: 'contact'
  },
  { 
    name: 'Contact Full Name', 
    variable: ':contact_full_name:', 
    description: "Recipient's full name (first + last)",
    category: 'contact'
  },
  { 
    name: 'Business Name', 
    variable: ':business_name:', 
    description: 'Name of your business/location',
    category: 'business'
  },
  { 
    name: 'Business Contact Name', 
    variable: ':business_contact_name:', 
    description: 'Name of the business contact (first name)',
    category: 'business'
  },
  { 
    name: 'Business Contact First Name', 
    variable: ':business_contact_first_name:', 
    description: 'First name of the business contact',
    category: 'business'
  },
  { 
    name: 'Business Contact Full Name', 
    variable: ':business_contact_full_name:', 
    description: 'Full name of the business contact',
    category: 'business'
  },
  { 
    name: 'Business Office Number', 
    variable: ':business_office_number:', 
    description: 'Business phone number from account',
    category: 'business'
  },
  { 
    name: 'Business Phone', 
    variable: ':business_phone:', 
    description: 'Business phone number (alternative)',
    category: 'business'
  },
  { 
    name: 'Business Email', 
    variable: ':business_email:', 
    description: 'Business email address',
    category: 'business'
  },
  { 
    name: 'Appointment Time', 
    variable: ':appointment_time:', 
    description: 'Scheduled appointment time',
    category: 'appointment'
  },
  { 
    name: 'Appointment Date', 
    variable: ':appointment_date:', 
    description: 'Scheduled appointment date',
    category: 'appointment'
  },
  { 
    name: 'New Post Highlight', 
    variable: ':new_post_highlight:', 
    description: 'Link to your most recent post',
    category: 'links'
  },
  { 
    name: 'Create Post Invite', 
    variable: ':create_post_invite:', 
    description: 'Invite customers to create a post',
    category: 'links'
  },
  { 
    name: 'Review Link', 
    variable: ':review_link:', 
    description: 'Link for customers to leave a review',
    category: 'links'
  },
  { 
    name: 'Friend Invite Link', 
    variable: ':friend_invite_link:', 
    description: 'Invite other businesses to join Everybrand',
    category: 'links'
  }
];
